import React, { useEffect, useState } from "react";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { GeofencePreviewMap } from "../../../components/maps/GeofencePreviewMap";

// Shown on the shift form when "require geofence" is on. Tells the admin where
// clock-in will actually be checked (the post's site, else the shift's earliest
// located activity), warns when that site has no geofence set (so it doesn't
// silently do nothing), and offers a read-only map preview on demand.
//
// siteId / siteName describe the primary site, or null when no
// located activity has been added yet.
function GeofenceStatus({ siteId, siteName }) {
  const authFetch = useAuthFetch();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(siteName || null);
  const [geofence, setGeofence] = useState(null);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShowMap(false);

    if (!siteId) {
      setGeofence(null);
      setName(null);
      return;
    }

    setLoading(true);
    authFetch(`${process.env.REACT_APP_API_URL}/v1/sites/${siteId}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const loc = Array.isArray(data) ? data[0] : data;
        const hasFence =
          loc &&
          loc.geofence_radius_m != null &&
          loc.geofence_latitude != null &&
          loc.geofence_longitude != null;
        setName(loc?.site_name || siteName || "this site");
        setGeofence(
          hasFence
            ? {
                latitude: loc.geofence_latitude,
                longitude: loc.geofence_longitude,
                radius_m: loc.geofence_radius_m,
              }
            : null,
        );
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setGeofence(null);
        setName(siteName || null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  if (!siteId) {
    return (
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        Add an activity to enable the geofence. The clock-in geofence is checked
        using the first actviity's site.
      </p>
    );
  }

  if (loading) {
    return (
      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        Checking {name || "site"}…
      </p>
    );
  }

  if (!geofence) {
    return (
      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        {name} doesn't have a geofence set. Clock-in cannot be restricted.
      </p>
    );
  }

  return (
    <div className="mt-2">
      <p className="text-xs text-gray-600 dark:text-gray-300">
        Clocking in will use <span className="font-medium">{name}</span>'s{" "}
        {geofence.radius_m} m geofence.{" "}
        <button
          type="button"
          onClick={() => setShowMap((v) => !v)}
          className="text-brand-primary underline"
        >
          {showMap ? "Hide map" : "Preview"}
        </button>
      </p>
      {showMap && (
        <div className="mt-2">
          <GeofencePreviewMap
            latitude={geofence.latitude}
            longitude={geofence.longitude}
            radius={geofence.radius_m}
          />
        </div>
      )}
    </div>
  );
}

export { GeofenceStatus };
